import {
  ApiDashboardElement,
  DimensionSource,
  IntervalJSON,
  JSONSerializable,
  SelectionSchemaMeasurePoint,
  Source,
  TimeseriesJSON,
} from '@highimpact-frontend/api-types';
import { ErrorStatus } from '@highimpact/dashboard-ui';
import { useSessionStorageString } from '@highimpact/react-hooks';
import {
  ControlPanel,
  ControlPanelSettings,
  PlotPreferences,
  ThresholdCatalogSnapshot,
  ThresholdPrefs,
  plotPreferencesFromSources,
  upgradePlotPreferences,
} from '@highimpact/scatterplot';
import { WidgetSourceSelector } from '@highimpact/source-selector';
import {
  ThresholdBundleCreateForm,
  ThresholdSelectorForm,
  buildAndFetchThresholdCatalog,
  buildThresholdDataQueryParameters,
  type UIThresholdModel,
} from '@highimpact/thresholds';
import { TimeRangeUnionForm } from '@highimpact/time-interval-selector';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MigratedPlotFormProps } from '../../../../forms/formHandlerShared';
import {
  ensureConfiguredTimeseries,
  getIntervals,
  isValid,
  setAdditionalProperties,
  setElementIndependentSourcesFromResolvedSources,
  setIntervals,
} from '../../../../state/dashboard-element-operations';
import { getDefaultIndependentDimensionKeys } from '../shared/buildWidgetDataSourceRequest';
import {
  buildIndependentDimensionSelectionFromElement,
  dimensionSelectionToAxisSources,
  resolveIndependentDimensionSelectionFromCatalog,
  syncIndependentDimensionSelectionFromCatalog,
} from '../shared/independentDimensionSelection';
import {
  PlotConfigFormDivider,
  PlotConfigFormShell,
  PlotConfigInvalidStatus,
  PlotConfigSection,
  PlotConfigSubmitButton,
} from '../shared/plotConfigFormLayout';
import {
  areAxisSourceSelectionsEqual,
  resolveSourcesFromCatalog,
} from '../shared/resolveSourcesFromCatalog';
import { useWidgetSourceSelection } from '../shared/useWidgetSourceSelection';

const TIMESERIES_DIMENSION_KEYS =
  getDefaultIndependentDimensionKeys('timeseries');

// -- type definitions --

type ThresholdDialogView = 'selector' | 'create' | 'edit-definition';
type ThresholdCoordinateSystem =
  | 'time_series'
  | 'cartesian_2d'
  | 'polar_2d'
  | 'cartesian_3d'
  | 'polar_3d';
type ThresholdRepresentationMode = 'sampled' | 'mesh';

// -- helper functions --

function normalizeThresholdLabel(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeThresholdCoordinateSystem(
  value: string
): ThresholdCoordinateSystem {
  const allowed: ThresholdCoordinateSystem[] = [
    'time_series',
    'cartesian_2d',
    'polar_2d',
    'cartesian_3d',
    'polar_3d',
  ];
  return allowed.includes(value as ThresholdCoordinateSystem)
    ? (value as ThresholdCoordinateSystem)
    : 'time_series';
}

function normalizeThresholdRepresentationMode(
  value: string
): ThresholdRepresentationMode {
  return value === 'mesh' ? 'mesh' : 'sampled';
}

function buildThresholdInitialDefinition(threshold: UIThresholdModel): {
  name: string;
  description: string;
  bound_kind: 'minimum' | 'maximum';
  threshold_type: string;
  parameters: Record<string, unknown>;
  evaluation_coordinate_system: ThresholdCoordinateSystem;
  representation_coordinate_system: ThresholdCoordinateSystem;
  representation_mode: ThresholdRepresentationMode;
  active_from: string;
  active_to: string;
} {
  return {
    name: threshold.label,
    description: threshold.description,
    bound_kind: threshold.boundKind,
    threshold_type: threshold.type,
    parameters: { ...threshold.rawParameters },
    evaluation_coordinate_system: normalizeThresholdCoordinateSystem(
      threshold.evaluationCoordinateSystem
    ),
    representation_coordinate_system: normalizeThresholdCoordinateSystem(
      threshold.representationCoordinateSystem
    ),
    representation_mode: normalizeThresholdRepresentationMode(
      threshold.representationMode
    ),
    active_from: threshold.activeFrom ?? '',
    active_to: threshold.activeTo ?? '',
  };
}

function buildThresholdCreationSourceGroups(
  sources: readonly Source[]
): Record<string, Source[]> {
  const labelOccurrences = new Map<string, number>();
  return sources.reduce<Record<string, Source[]>>((acc, source) => {
    let baseLabel = source.label?.trim();
    if (!baseLabel || baseLabel.length === 0) {
      baseLabel = `Source ${source.measure_point_id}-${source.measure_type_id}`;
    }
    const prev = labelOccurrences.get(baseLabel) ?? 0;
    labelOccurrences.set(baseLabel, prev + 1);
    let groupLabel = baseLabel;
    if (prev > 0) {
      groupLabel = `${baseLabel} (${prev + 1})`;
    }
    return {
      ...acc,
      [groupLabel]: [source],
    };
  }, {});
}

function getThresholdDefinitionIdsFromAssignments(
  selectedAssignmentIds: readonly number[],
  thresholds: readonly UIThresholdModel[]
): number[] {
  const selectedAssignmentIdSet = new Set(selectedAssignmentIds);
  const thresholdDefinitionIds = thresholds
    .filter((threshold) => selectedAssignmentIdSet.has(threshold.id))
    .map((threshold) => threshold.thresholdId);

  return [...new Set(thresholdDefinitionIds)];
}

function asPlotPreferences(config: ApiDashboardElement): PlotPreferences {
  return upgradePlotPreferences(
    (config.properties as TimeseriesJSON).additional_properties as
      | PlotPreferences
      | undefined
  );
}

function filterRecordByThresholdIds<T>(
  input: Record<number, T> | undefined,
  selectedIds: ReadonlySet<number>
): Record<number, T> | undefined {
  if (!input) {
    return undefined;
  }

  const entries = Object.entries(input).filter(([key]) =>
    selectedIds.has(Number(key))
  );

  return entries.length > 0
    ? Object.fromEntries(entries.map(([key, value]) => [Number(key), value]))
    : undefined;
}

function resolveTargetCurveIdsByThreshold(
  thresholds: readonly UIThresholdModel[],
  variableMappingsByThresholdId: Record<number, Record<string, string>>,
  validCurveIds: ReadonlySet<string>
): Record<number, string[]> | undefined {
  const entries = thresholds
    .map((threshold) => {
      const mapping = variableMappingsByThresholdId[threshold.id] ?? {};
      const dependentVariables = threshold.variableDetails
        .filter((variable) => variable.role === 'dependent')
        .map((variable) => mapping[variable.variableName])
        .filter((value): value is string => typeof value === 'string');
      const fallbackValue = mapping.value;
      const targetCurveIds = [
        ...new Set(
          [
            ...dependentVariables,
            ...(fallbackValue ? [fallbackValue] : []),
          ].filter((curveId): curveId is string => validCurveIds.has(curveId))
        ),
      ];

      return [threshold.id, targetCurveIds] as const;
    })
    .filter(([, targetCurveIds]) => targetCurveIds.length > 0);

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function resolveLineShapeByThresholdAssignmentId(
  thresholds: readonly UIThresholdModel[]
): Record<number, 'linear' | 'vhv' | 'hvh' | 'vh' | 'hv'> | undefined {
  const entries = thresholds.map(
    (threshold) =>
      [threshold.id, threshold.type === 'piecewise' ? 'hv' : 'linear'] as const
  );

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function toThresholdCatalogSnapshot(
  threshold: UIThresholdModel
): ThresholdCatalogSnapshot {
  return {
    ...threshold,
    variables: [...threshold.variables],
    variableDetails: threshold.variableDetails.map((variable) => ({
      ...variable,
    })),
    defaultStyle: threshold.defaultStyle ? { ...threshold.defaultStyle } : null,
    availableStyles: threshold.availableStyles.map((style) => ({ ...style })),
    sourceBindings: threshold.sourceBindings.map((binding) => ({ ...binding })),
  };
}

function toUIThresholdModel(
  threshold: ThresholdCatalogSnapshot
): UIThresholdModel {
  return {
    ...threshold,
    variables: [...threshold.variables],
    variableDetails: threshold.variableDetails.map((variable) => ({
      ...variable,
    })),
    defaultStyle: threshold.defaultStyle ? { ...threshold.defaultStyle } : null,
    availableStyles: threshold.availableStyles.map((style) => ({ ...style })),
    sourceBindings: threshold.sourceBindings.map((binding) => ({ ...binding })),
  };
}

// -- main component --

function areMeasurePointPathsEqual(
  left: readonly SelectionSchemaMeasurePoint[],
  right: readonly SelectionSchemaMeasurePoint[]
): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every(
    (path, index) =>
      path.measure_point_id === right[index]?.measure_point_id &&
      path.measure_type_id === right[index]?.measure_type_id
  );
}

function TimeSeriesForm({
  config,
  project_id,
  site_id,
  campaigns,
  onSubmit,
  onChange,
}: MigratedPlotFormProps) {
  const scope = useMemo(() => ({ project_id, site_id }), [project_id, site_id]);
  const { loading, error, data } = useWidgetSourceSelection({
    widget: 'timeseries',
    scope,
  });

  const configDataSourceKey = useMemo(
    () => JSON.stringify(config.data_source ?? []),
    [config.data_source]
  );

  const initialDimensionSelection = useMemo(
    () =>
      buildIndependentDimensionSelectionFromElement(
        data && data.response_type === 'dimensions' ? data : null,
        config,
        TIMESERIES_DIMENSION_KEYS,
        (sources) => ({ unique: [...sources] })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configDataSourceKey, data]
  );

  const [dimensionSelection, setDimensionSelection] = useState<
    Record<string, DimensionSource[]>
  >(() => initialDimensionSelection);
  const dimensionSelectionRef = useRef(dimensionSelection);
  dimensionSelectionRef.current = dimensionSelection;
  const selectedUniqueSources = useMemo(
    () => dimensionSelectionToAxisSources(dimensionSelection).unique ?? [],
    [dimensionSelection]
  );
  const [submitCount, setSubmitCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [validation, setValidation] = useState(isValid(config));
  const [isThresholdDialogOpen, setIsThresholdDialogOpen] = useState(false);
  const [isThresholdCatalogLoading, setIsThresholdCatalogLoading] =
    useState(false);
  const [thresholdCatalogError, setThresholdCatalogError] = useState<
    string | null
  >(null);
  const [thresholdCatalog, setThresholdCatalog] = useState<UIThresholdModel[]>(
    []
  );
  const [draftThresholdIds, setDraftThresholdIds] = useState<number[]>([]);
  const [pendingCreatedThresholdLabel, setPendingCreatedThresholdLabel] =
    useState<string | null>(null);
  const [thresholdDialogView, setThresholdDialogView] =
    useState<ThresholdDialogView>('selector');
  const [thresholdToEdit, setThresholdToEdit] =
    useState<UIThresholdModel | null>(null);
  const [token] = useSessionStorageString('security_token', '');

  const lastDimensionSeedKeyRef = useRef<string>('');

  useEffect(() => {
    const seedKey = `${configDataSourceKey}|${data ? 'loaded' : 'pending'}`;
    if (seedKey === lastDimensionSeedKeyRef.current) {
      return;
    }

    lastDimensionSeedKeyRef.current = seedKey;

    setDimensionSelection((previousSelection) => {
      if (!data) {
        return initialDimensionSelection;
      }

      const syncedSelection = syncIndependentDimensionSelectionFromCatalog(
        data,
        TIMESERIES_DIMENSION_KEYS,
        initialDimensionSelection
      );

      return areAxisSourceSelectionsEqual(
        dimensionSelectionToAxisSources(previousSelection),
        dimensionSelectionToAxisSources(syncedSelection)
      )
        ? previousSelection
        : syncedSelection;
    });
  }, [configDataSourceKey, data, initialDimensionSelection]);

  const thresholdCreationSelectedSources = useMemo(() => {
    const selectedSourcePaths =
      (config.data_source as SelectionSchemaMeasurePoint[]) ?? [];
    const fallbackSources =
      dimensionSelectionToAxisSources(dimensionSelection).unique ?? [];

    return resolveSourcesFromCatalog(
      data,
      selectedSourcePaths,
      fallbackSources
    );
  }, [config.data_source, data, dimensionSelection]);

  const thresholdCreationAvailableSources = useMemo<
    Record<string, Source[]>
  >(() => {
    const selectedSources = thresholdCreationSelectedSources;
    if (selectedSources.length === 0) {
      return {};
    }

    return buildThresholdCreationSourceGroups(selectedSources);
  }, [thresholdCreationSelectedSources]);
  const hasThresholdCreationSources = useMemo(
    () => Object.keys(thresholdCreationAvailableSources).length > 0,
    [thresholdCreationAvailableSources]
  );

  const resolvedPlotPreferences = useMemo(
    () => asPlotPreferences(config),
    [config]
  );
  const selectedThresholdIds = useMemo(
    () =>
      resolvedPlotPreferences.thresholdPrefs?.request
        ?.selectedThresholdAssignmentIds ?? [],
    [resolvedPlotPreferences.thresholdPrefs?.request]
  );
  const selectedThresholdIdSet = useMemo(
    () => new Set(selectedThresholdIds),
    [selectedThresholdIds]
  );
  const selectedThresholds = useMemo(() => {
    const persistedCatalog =
      resolvedPlotPreferences.thresholdPrefs?.request
        ?.selectedThresholdCatalog ?? [];

    const uniqueByThresholdId = new Map<number, ThresholdCatalogSnapshot>();

    persistedCatalog.forEach((threshold) => {
      if (!selectedThresholdIdSet.has(threshold.id)) {
        return;
      }

      if (!uniqueByThresholdId.has(threshold.thresholdId)) {
        uniqueByThresholdId.set(threshold.thresholdId, threshold);
      }
    });

    return [...uniqueByThresholdId.values()];
  }, [resolvedPlotPreferences.thresholdPrefs, selectedThresholdIdSet]);

  const applyDimensionSelection = useCallback(
    (nextSelection: Record<string, DimensionSource[]>) => {
      const configuredElement = ensureConfiguredTimeseries(config);
      const resolvedSelection = resolveIndependentDimensionSelectionFromCatalog(
        data,
        nextSelection,
        TIMESERIES_DIMENSION_KEYS
      );
      const resolvedSources = resolvedSelection.unique ?? [];
      const newConfig = setElementIndependentSourcesFromResolvedSources(
        resolvedSelection,
        TIMESERIES_DIMENSION_KEYS,
        configuredElement
      );
      const previousPreferences = (
        configuredElement.properties as {
          additional_properties?: ControlPanelSettings;
        }
      ).additional_properties;
      const newPlotPreferences = plotPreferencesFromSources(
        resolvedSources,
        false,
        undefined,
        previousPreferences
      );
      const newConfigWithPreferences = setAdditionalProperties(
        newPlotPreferences as unknown as JSONSerializable,
        newConfig
      );
      const nextPaths =
        (newConfigWithPreferences.data_source as SelectionSchemaMeasurePoint[]) ??
        [];
      const currentPaths =
        (configuredElement.data_source as SelectionSchemaMeasurePoint[]) ?? [];
      const hasPersistedPreferences = Boolean(
        (
          configuredElement.properties as {
            additional_properties?: ControlPanelSettings;
          }
        ).additional_properties
      );
      if (
        areMeasurePointPathsEqual(nextPaths, currentPaths) &&
        hasPersistedPreferences
      ) {
        return;
      }
      setValidation(isValid(newConfigWithPreferences));
      onChange(newConfigWithPreferences);
    },
    [config, data, onChange]
  );

  const timeseriesControlPanelSettings =
    useMemo((): ControlPanelSettings | null => {
      const uniqueCount = dimensionSelection.unique?.length ?? 0;

      if (uniqueCount === 0) {
        return null;
      }

      const fromConfig = (
        config.properties as { additional_properties?: ControlPanelSettings }
      ).additional_properties;

      if (fromConfig) {
        return fromConfig;
      }

      const resolvedUnique =
        dimensionSelectionToAxisSources(dimensionSelection).unique ?? [];
      const resolvedSources = resolveSourcesFromCatalog(
        data,
        resolvedUnique.map((source) => ({
          measure_point_id: source.measure_point_id,
          measure_type_id: source.measure_type_id,
        })),
        resolvedUnique
      );

      if (resolvedSources.length === 0) {
        return null;
      }

      return plotPreferencesFromSources(
        resolvedSources,
        false
      ) as ControlPanelSettings;
    }, [config.properties, data, dimensionSelection]);

  const handleIntervalsUpdate = (intervals: IntervalJSON[]) => {
    const newConfig = setIntervals(
      intervals,
      ensureConfiguredTimeseries(config)
    );
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const fetchThresholdCatalog = useCallback(() => {
    const dataSource = selectedUniqueSources.map((source) => ({
      measure_point_id: source.measure_point_id,
      measure_type_id: source.measure_type_id,
    }));

    if (dataSource.length === 0) {
      setThresholdCatalog([]);
      setThresholdCatalogError(
        'Sélectionnez au moins une source avant de configurer les seuils.'
      );
      return;
    }

    setIsThresholdCatalogLoading(true);

    const intervalsForThresholdRequest = getIntervals(config) ?? [];

    void buildAndFetchThresholdCatalog(
      {
        plotType: 'timeseries',
        dataSource,
        timeIntervals: intervalsForThresholdRequest,
      },
      { token }
    )
      .then((items) => {
        setThresholdCatalog(items);

        if (pendingCreatedThresholdLabel) {
          const normalizedPendingLabel = normalizeThresholdLabel(
            pendingCreatedThresholdLabel
          );
          const matchingThreshold = items
            .filter(
              (threshold) =>
                normalizeThresholdLabel(threshold.label) ===
                normalizedPendingLabel
            )
            .sort((left, right) => right.id - left.id)[0];

          if (matchingThreshold) {
            setDraftThresholdIds((current) =>
              current.includes(matchingThreshold.thresholdId)
                ? current
                : [...current, matchingThreshold.thresholdId]
            );
            setPendingCreatedThresholdLabel(null);
          }
        }
      })
      .catch((reason: unknown) => {
        setThresholdCatalogError(
          reason instanceof Error
            ? reason.message
            : 'Impossible de charger le catalogue de seuils.'
        );
      })
      .finally(() => {
        setIsThresholdCatalogLoading(false);
      });
  }, [config, selectedUniqueSources, pendingCreatedThresholdLabel, token]);

  const handleOpenThresholdDialog = useCallback(() => {
    const persistedCatalog = (
      resolvedPlotPreferences.thresholdPrefs?.request
        ?.selectedThresholdCatalog ?? []
    ).map(toUIThresholdModel);

    setDraftThresholdIds(
      getThresholdDefinitionIdsFromAssignments(
        selectedThresholdIds,
        persistedCatalog
      )
    );
    setThresholdCatalog(persistedCatalog);
    setThresholdCatalogError(null);
    setThresholdDialogView('selector');
    setThresholdToEdit(null);
    setIsThresholdDialogOpen(true);
    fetchThresholdCatalog();
  }, [
    fetchThresholdCatalog,
    resolvedPlotPreferences.thresholdPrefs,
    selectedThresholdIds,
  ]);

  const handleCloseThresholdDialog = useCallback(() => {
    setIsThresholdDialogOpen(false);
    setThresholdCatalogError(null);
    setThresholdDialogView('selector');
    setPendingCreatedThresholdLabel(null);
    setThresholdToEdit(null);
  }, []);

  const handleOpenThresholdCreation = useCallback(() => {
    setThresholdCatalogError(null);
    setThresholdDialogView('create');
    setThresholdToEdit(null);
  }, []);

  const handleCancelThresholdCreation = useCallback(() => {
    setThresholdDialogView('selector');
    setThresholdToEdit(null);
  }, []);

  const handleOpenThresholdEdition = useCallback(
    (threshold: UIThresholdModel) => {
      setThresholdCatalogError(null);
      setThresholdToEdit(threshold);
      setThresholdDialogView('edit-definition');
    },
    []
  );

  const handleCancelThresholdEdition = useCallback(() => {
    setThresholdDialogView('selector');
    setThresholdToEdit(null);
  }, []);

  const handleThresholdCreated = useCallback(
    (payload: { definition: { name: string } }) => {
      setPendingCreatedThresholdLabel(payload.definition.name);
      setThresholdCatalogError(null);
      setThresholdDialogView('selector');
      setThresholdToEdit(null);
      fetchThresholdCatalog();
    },
    [fetchThresholdCatalog]
  );

  const handleThresholdUpdated = useCallback(() => {
    setThresholdCatalogError(null);
    setThresholdDialogView('selector');
    setThresholdToEdit(null);
    fetchThresholdCatalog();
  }, [fetchThresholdCatalog]);

  const handleConfirmThresholdSelection = useCallback(() => {
    const selectedThresholdDefinitionIdSet = new Set(draftThresholdIds);
    const selectedCatalogItems = thresholdCatalog.filter((threshold) =>
      selectedThresholdDefinitionIdSet.has(threshold.thresholdId)
    );
    const selectedAssignmentIdSet = new Set(
      selectedCatalogItems.map((threshold) => threshold.id)
    );
    const previousThresholdPrefs = resolvedPlotPreferences.thresholdPrefs;

    let nextThresholdPrefs: ThresholdPrefs | undefined;

    if (selectedCatalogItems.length > 0) {
      const thresholdRequest = buildThresholdDataQueryParameters({
        selectedThresholds: selectedCatalogItems,
        selectedSources: selectedUniqueSources.map((source) => ({
          measure_point_id: source.measure_point_id,
          measure_type_id: source.measure_type_id,
        })),
        plotType: 'timeseries',
      });

      const variableMappingsByThresholdId = Object.fromEntries(
        thresholdRequest.threshold_requests.map((request) => [
          request.threshold_scope_assignment_id,
          request.variables,
        ])
      ) as Record<number, Record<string, string>>;

      nextThresholdPrefs = {
        request: {
          ...previousThresholdPrefs?.request,
          selectedThresholdAssignmentIds: selectedCatalogItems.map(
            (threshold) => threshold.id
          ),
          selectedThresholdCatalog: selectedCatalogItems.map(
            toThresholdCatalogSnapshot
          ),
          variableMappingsByThresholdId,
        },
        visualization: {
          enabled: previousThresholdPrefs?.visualization?.enabled ?? true,
          markerBorderOpacity:
            previousThresholdPrefs?.visualization?.markerBorderOpacity,
          markerBorderWidthPx:
            previousThresholdPrefs?.visualization?.markerBorderWidthPx,
          styleSelectionByThresholdId: filterRecordByThresholdIds(
            previousThresholdPrefs?.visualization?.styleSelectionByThresholdId,
            selectedAssignmentIdSet
          ),
          lineShapeByThresholdAssignmentId:
            resolveLineShapeByThresholdAssignmentId(selectedCatalogItems),
          targetCurveIdsByThresholdAssignmentId:
            resolveTargetCurveIdsByThreshold(
              selectedCatalogItems,
              variableMappingsByThresholdId,
              new Set(Object.keys(resolvedPlotPreferences.curvePrefs))
            ),
        },
      };
    }

    const newConfig = setAdditionalProperties(
      {
        ...resolvedPlotPreferences,
        thresholdPrefs: nextThresholdPrefs,
      } as unknown as JSONSerializable,
      ensureConfiguredTimeseries(config)
    );

    setValidation(isValid(newConfig));
    onChange(newConfig);
    setIsThresholdDialogOpen(false);
    setThresholdCatalogError(null);
  }, [
    config,
    selectedUniqueSources,
    draftThresholdIds,
    onChange,
    resolvedPlotPreferences,
    thresholdCatalog,
  ]);

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setSubmitCount(submitCount + 1);
    if (validation[0]) {
      setIsSending(true);
      onSubmit(config).finally(() => setIsSending(false));
    }
  };

  const dataSourceErrors = validation?.[1]?.dataSource ?? [];
  const dataSourceHasError: ErrorStatus =
    submitCount > 0
      ? { tag: 'Submited', errors: dataSourceErrors }
      : { tag: 'Not submitted' };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PlotConfigFormShell onSubmit={handleSubmit}>
      <WidgetSourceSelector
        data={data}
        loading={loading}
        error={error}
        initialIndependentSelection={dimensionSelection}
        onIndependentDimensionSelect={(_dimensionKey, selected) => {
          const nextSelection = {
            ...dimensionSelectionRef.current,
            unique: selected,
          };
          setDimensionSelection(nextSelection);
          applyDimensionSelection(nextSelection);
        }}
        matchControlPanelStyle
      />

      <PlotConfigInvalidStatus error={dataSourceHasError} />

      <PlotConfigFormDivider />

      <TimeRangeUnionForm
        onChange={handleIntervalsUpdate}
        campaigns={campaigns}
        noDivision
        noRelativeFuture={false}
        defaultIntervals={getIntervals(config)}
        matchControlPanelStyle
      />

      <PlotConfigFormDivider />

      <PlotConfigSection title="Seuils">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Sélectionnez les seuils à évaluer et à afficher sur la série
          temporelle.
        </Typography>

        <Stack spacing={1.25}>
          {selectedThresholds.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Aucun seuil sélectionné.
            </Typography>
          ) : (
            <List dense disablePadding>
              {selectedThresholds.map((threshold) => (
                <ListItem key={threshold.id} disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={threshold.label}
                    secondary={threshold.parameterSummary}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Box>
            <Button variant="outlined" onClick={handleOpenThresholdDialog}>
              Configurer les seuils
            </Button>
          </Box>
        </Stack>
      </PlotConfigSection>

      {timeseriesControlPanelSettings ? <PlotConfigFormDivider /> : null}

      {timeseriesControlPanelSettings && (
        <ControlPanel
          settings={timeseriesControlPanelSettings}
          plotType="timeseries"
          onSettingsChanged={(next) => {
            const newConfig = setAdditionalProperties(
              next as unknown as JSONSerializable,
              ensureConfiguredTimeseries(config)
            );
            setValidation(isValid(newConfig));
            onChange(newConfig);
          }}
        />
      )}

      <PlotConfigSubmitButton
        isSending={isSending}
        loading={loading}
        disabled={
          dataSourceHasError.tag === 'Submited' &&
          dataSourceHasError.errors.length > 0
        }
      />

      <Dialog
        open={isThresholdDialogOpen}
        onClose={handleCloseThresholdDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Configuration des seuils
          <IconButton
            aria-label="Fermer la fenêtre de configuration des seuils"
            onClick={handleCloseThresholdDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {thresholdDialogView === 'selector' ? (
            <Stack spacing={2}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="text.secondary">
                  Sélectionnez les seuils à appliquer à la série temporelle.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleOpenThresholdCreation}
                  disabled={
                    isThresholdCatalogLoading || !hasThresholdCreationSources
                  }
                >
                  Créer un seuil
                </Button>
              </Stack>

              {isThresholdCatalogLoading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={28} />
                </Box>
              ) : null}

              {thresholdCatalogError ? (
                <Alert severity="error">{thresholdCatalogError}</Alert>
              ) : null}

              <ThresholdSelectorForm
                thresholds={thresholdCatalog}
                selectedThresholdIds={draftThresholdIds}
                onSelectionChange={setDraftThresholdIds}
                onEditRequest={handleOpenThresholdEdition}
                selectionIdMode="threshold"
                deduplicateBySelectionId
                title="Seuils disponibles"
                helperText="Confirmez pour enregistrer cette sélection dans les préférences du widget."
                disabled={isThresholdCatalogLoading}
              />
            </Stack>
          ) : thresholdDialogView === 'create' ? (
            <ThresholdBundleCreateForm
              availableSources={thresholdCreationAvailableSources}
              allowedCoordinateSystems={['time_series']}
              allowedRepresentationModes={['sampled']}
              submitToApi
              title="Créer un seuil"
              onCancel={handleCancelThresholdCreation}
              onCreated={handleThresholdCreated}
            />
          ) : thresholdToEdit ? (
            <ThresholdBundleCreateForm
              mode="edit-definition"
              thresholdId={thresholdToEdit.thresholdId}
              initialDefinition={buildThresholdInitialDefinition(
                thresholdToEdit
              )}
              availableSources={thresholdCreationAvailableSources}
              allowedCoordinateSystems={['time_series']}
              allowedRepresentationModes={['sampled']}
              submitToApi
              title="Modifier le seuil"
              onCancel={handleCancelThresholdEdition}
              onUpdated={handleThresholdUpdated}
            />
          ) : (
            <Alert severity="warning">
              Impossible de charger le seuil à modifier.
            </Alert>
          )}
        </DialogContent>
        {thresholdDialogView === 'selector' ? (
          <DialogActions>
            <Button onClick={handleCloseThresholdDialog}>Annuler</Button>
            <Button
              variant="contained"
              onClick={handleConfirmThresholdSelection}
              disabled={isThresholdCatalogLoading}
            >
              Confirmer
            </Button>
          </DialogActions>
        ) : null}
      </Dialog>
    </PlotConfigFormShell>
  );
}

export default TimeSeriesForm;
